import { SyncInsuranceListenerCallback } from './SyncListener'
import {
  CommerceBenefitPlanResponse,
  CommerceInsuranceDataResponse,
  CommerceProductQuoteRequestItem,
  CommerceProductQuoteResponse,
  ServiceStatusCallback,
  ServiceType,
} from '@connector/types'

export const CUSTOM_EVENT_NAME = {
  APP: 'db:insuranceEvent',
  DRAWER: 'db:insuranceDrawerEvent',
  WIDGET: 'db:insuranceWidgetEvent',
} as const

/**
 * Custom events from commerce app
 */
export type InsuranceEventType =
  | 'onLogin'
  | 'onLogout'
  | 'onSessionError'
  | 'onOrderComplete'
  | 'onCartBundleChange'
  | 'onCartAddItem'
  | 'onCartRemoveItem'
  | 'onCartEditItem'
  | 'onCartChangeQuantity'
  | 'onCartApplySubscription'
  | 'onCartApplyPromoCode'
  | 'onCartRemovePromoCode'
  | 'onServiceStatus'
  | 'onRequestProductQuote'
  | 'onRequestBenefitPlan'
  | 'onAddSyncInsuranceListener'
  | 'onRemoveSyncInsuranceListener'
  | 'onAddInsuranceBenefits'
  | 'onRemoveInsuranceBenefits'
  | 'onRequestInsuranceData'

export type DrawerEventType = 'onOpenDrawer'

export type VoidCallback = () => void

export type LoginEvent = {
  type: 'onLogin'
}

export type LogoutEvent = {
  type: 'onLogout'
  payload?: VoidCallback
}

export type OpenDrawerEventPayload = {
  target?: string
  navigateState?: object
  viewOnly?: boolean
  callback?: () => void
}

export type OpenDrawerEvent = {
  type: 'onOpenDrawer'
  payload?: OpenDrawerEventPayload
}

export type SessionErrorEvent = {
  type: 'onSessionError'
  payload?: unknown
}

export type OrderCompleteEvent = {
  type: 'onOrderComplete'
}

export type CartBundleChangeEvent = {
  type: 'onCartBundleChange'
}

export type CartAddItemEvent = {
  type: 'onCartAddItem'
  payload: VoidCallback
}

export type CartRemoveItemEvent = {
  type: 'onCartRemoveItem'
  payload: VoidCallback
}

export type CartChangeQuantityEvent = {
  type: 'onCartChangeQuantity'
  payload: VoidCallback
}

export type CartEditItemEvent = {
  type: 'onCartEditItem'
  payload: VoidCallback
}

export type CartApplySubscriptionEvent = {
  type: 'onCartApplySubscription'
  payload: VoidCallback
}

export type CartApplyPromoCodeEvent = {
  type: 'onCartApplyPromoCode'
  payload: VoidCallback
}

export type CartRemovePromoCodeEvent = {
  type: 'onCartRemovePromoCode'
  payload: VoidCallback
}

export type ServiceStatusEvent = {
  type: 'onServiceStatus'
  payload: {
    serviceType: ServiceType
    handler: ServiceStatusCallback
  }
}

export type RequestProductQuoteEvent = {
  type: 'onRequestProductQuote'
  payload: {
    request: CommerceProductQuoteRequestItem[]
    handler: (response: CommerceProductQuoteResponse) => void
  }
}

export type AddSyncInsuranceListenerEvent = {
  type: 'onAddSyncInsuranceListener'
  payload?: SyncInsuranceListenerCallback
}

export type RemoveSyncInsuranceListenerEvent = {
  type: 'onRemoveSyncInsuranceListener'
  payload?: SyncInsuranceListenerCallback
}

export type AddInsuranceBenefitsEvent = {
  type: 'onAddInsuranceBenefits'
}

export type RemoveInsuranceBenefitsEvent = {
  type: 'onRemoveInsuranceBenefits'
}

export type RequestBenefitPlanEvent = {
  type: 'onRequestBenefitPlan'
  payload: (response: CommerceBenefitPlanResponse) => void
}

export type RequestInsuranceDataEvent = {
  type: 'onRequestInsuranceData'
  payload: (response: CommerceInsuranceDataResponse) => void
}

export type InsuranceEvent =
  | ServiceStatusEvent
  | LoginEvent
  | LogoutEvent
  | SessionErrorEvent
  | OrderCompleteEvent
  | CartBundleChangeEvent
  | CartAddItemEvent
  | CartRemoveItemEvent
  | CartEditItemEvent
  | CartChangeQuantityEvent
  | CartApplySubscriptionEvent
  | CartApplyPromoCodeEvent
  | CartRemovePromoCodeEvent
  | ServiceStatusEvent
  | RequestProductQuoteEvent
  | AddSyncInsuranceListenerEvent
  | RemoveSyncInsuranceListenerEvent
  | AddInsuranceBenefitsEvent
  | RemoveInsuranceBenefitsEvent
  | RequestBenefitPlanEvent
  | RequestInsuranceDataEvent

export type DrawerEvent = OpenDrawerEvent
